import moment, { Moment } from 'moment';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import {
  DateFieldFilterAbsoluteOperationType,
  DateFieldFilterOperationType,
  DateFieldFilterRelativeOperationType,
  RelativeDirection,
} from 'components/organisms/tasks/filters/date';
import { unreachable } from 'utils/unreachable';

export enum Range {
  ThisWeek = 'this_week',
  LastWeek = 'last_week',
  ThisMonth = 'this_month',
  LastMonth = 'last_month',
  Last12Months = 'last_12_months',
  Last10Years = 'last_10_year',
  ThisQuarter = 'this_quarter',
  LastQuarter = 'last_quarter',
  ThisYear = 'this_year',
  Last30Days = 'last_30_days',
  Last60Days = 'last_60_days',
  Last90Days = 'last_90_days',
  Last180Days = 'last_180_days',
  Custom = 'custom',
  Advanced = 'advanced',
  Anytime = 'anytime',
}
interface ThisWeekRange {
  type: Range.ThisWeek;
}
interface LastWeekRange {
  type: Range.LastWeek;
}
interface ThisMonthRange {
  type: Range.ThisMonth;
}
interface LastMonthRange {
  type: Range.LastMonth;
}
interface ThisQuarterRange {
  type: Range.ThisQuarter;
}
interface LastQuarterRange {
  type: Range.LastQuarter;
}

interface Last30Days {
  type: Range.Last30Days;
}
interface Last60Days {
  type: Range.Last60Days;
}
interface Last90Days {
  type: Range.Last90Days;
}
interface Last180Days {
  type: Range.Last180Days;
}

interface Last12Months {
  type: Range.Last12Months;
}

interface LastYearRange {
  type: Range.Last10Years;
}

interface ThisYearRange {
  type: Range.ThisYear;
}

interface CustomRange {
  type: Range.Custom;
  startDate: Moment;
  endDate: Moment;
}

interface AdvancedRange {
  type: Range.Advanced;
  startDate: Moment;
  endDate: Moment;
}

interface AnythingRange {
  type: Range.Anytime;
}

export type DateRange =
  | ThisWeekRange
  | LastWeekRange
  | ThisMonthRange
  | LastMonthRange
  | ThisQuarterRange
  | LastQuarterRange
  | Last30Days
  | Last60Days
  | Last90Days
  | Last180Days
  | Last12Months
  | ThisYearRange
  | LastYearRange
  | CustomRange
  | AnythingRange
  | AdvancedRange;

export const DefaultRangeOptions = [
  Range.ThisWeek,
  Range.LastWeek,
  Range.ThisMonth,
  Range.LastMonth,
  Range.ThisQuarter,
  Range.LastQuarter,
  Range.Last90Days,
  Range.Custom,
  Range.Advanced,
  Range.Anytime,
];

export const getRangeObject = (
  range: Range,
  startDate?: Moment,
  endDate?: Moment,
): DateRange => {
  switch (range) {
    case Range.ThisWeek:
    case Range.LastWeek:
    case Range.ThisMonth:
    case Range.LastMonth:
    case Range.ThisQuarter:
    case Range.LastQuarter:
    case Range.Last30Days:
    case Range.Last60Days:
    case Range.Last90Days:
    case Range.Last180Days:
    case Range.Last12Months:
    case Range.Last10Years:
    case Range.ThisYear:
    case Range.Anytime:
      return { type: range };
    case Range.Custom:
    case Range.Advanced:
      return startDate && endDate
        ? { type: range, endDate, startDate }
        : unreachable(range as never);
    default:
      return unreachable(range as never);
  }
};

export const getValueFromDateRange = (range: DateRange): [Moment, Moment] => {
  switch (range.type) {
    case Range.ThisWeek:
      return [moment().startOf('week'), moment().endOf('week')];
    case Range.LastWeek:
      return [
        moment().subtract(1, 'week').startOf('week'),
        moment().subtract(1, 'week').endOf('week'),
      ];
    case Range.ThisMonth:
      return [moment().startOf('month'), moment().endOf('month')];
    case Range.LastMonth:
      return [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ];
    case Range.ThisQuarter:
      return [moment().startOf('quarter'), moment().endOf('quarter')];
    case Range.LastQuarter:
      return [
        moment().subtract(1, 'quarter').startOf('quarter'),
        moment().subtract(1, 'quarter').endOf('quarter'),
      ];
    case Range.Last30Days:
      return [
        moment().subtract(30, 'days').startOf('day'),
        moment().endOf('day'),
      ];
    case Range.Last60Days:
      return [
        moment().subtract(60, 'days').startOf('day'),
        moment().endOf('day'),
      ];
    case Range.Last90Days:
      return [
        moment().subtract(90, 'days').startOf('day'),
        moment().endOf('day'),
      ];
    case Range.Last180Days:
      return [
        moment().subtract(180, 'days').startOf('day'),
        moment().endOf('day'),
      ];
    case Range.Last12Months:
      return [
        moment().subtract(365, 'days').startOf('day'),
        moment().endOf('day'),
      ];
    case Range.Last10Years:
      return [
        moment().subtract(10, 'years').startOf('day'),
        moment().endOf('day'),
      ];
    case Range.ThisYear:
      return [moment().startOf('year'), moment().endOf('year')];
    case Range.Anytime:
      return [moment(0), moment()];
    case Range.Custom:
    case Range.Advanced:
      return [range.startDate, range.endDate];
    default:
      return unreachable(range as never);
  }
};

const getCustomRangeObject = (startDate: Moment, endDate: Moment) =>
  getRangeObject(Range.Custom, startDate, endDate);

export const getRangeObjectForRelativeTypes = (
  type: DateFieldFilterRelativeOperationType,
  value: number,
  direction: RelativeDirection,
): DateRange => {
  if (direction === RelativeDirection.Ago) {
    switch (type) {
      case DateFieldFilterOperationType.IsMoreThan:
        return {
          type: Range.Advanced,
          startDate: moment(0),
          endDate: moment().subtract(value, 'days'),
        };
      case DateFieldFilterOperationType.IsLessThan:
        return {
          type: Range.Advanced,
          endDate: moment(),
          startDate: moment().subtract(value, 'days'),
        };

      case DateFieldFilterOperationType.IsExactly:
        return {
          type: Range.Advanced,
          startDate: moment().subtract(value, 'days'),
          endDate: moment().subtract(value, 'days'),
        };

      default:
        return unreachable(type);
    }
  } else if (direction === RelativeDirection.FromNow) {
    switch (type) {
      case DateFieldFilterOperationType.IsMoreThan:
        return {
          type: Range.Advanced,
          endDate: moment().add(100, 'years'),
          startDate: moment().add(value, 'days'),
        };
      case DateFieldFilterOperationType.IsLessThan:
        return {
          type: Range.Advanced,
          startDate: moment(),
          endDate: moment().add(value, 'days'),
        };

      case DateFieldFilterOperationType.IsExactly:
        return {
          type: Range.Advanced,
          startDate: moment().add(value, 'days'),
          endDate: moment().add(value, 'days'),
        };

      default:
        return unreachable(type);
    }
  } else {
    return unreachable(direction);
  }
};

export const getRangeObjectForAbsoluteTypes = (
  type: DateFieldFilterAbsoluteOperationType,
  value: moment.Moment,
): DateRange => {
  switch (type) {
    case DateFieldFilterOperationType.IsAfter:
      return {
        type: Range.Advanced,
        startDate: moment.utc(`${value.format('YYYY-MM-DD')}T00:00:00Z`),
        endDate: moment().utc(),
      };

    case DateFieldFilterOperationType.IsBefore:
      return {
        type: Range.Advanced,
        endDate: moment.utc(`${value.format('YYYY-MM-DD')}T00:00:00Z`),
        startDate: moment(0).utc(),
      };

    case DateFieldFilterOperationType.IsOn:
      return {
        type: Range.Advanced,
        endDate: moment(),
        startDate: moment(),
      };

    default:
      return unreachable(type);
  }
};

const getPreviousRangeForCustom = (range: CustomRange): DateRange => {
  const startDate = moment(range.startDate);
  const endDate = moment(range.endDate);
  const diff = endDate.diff(startDate, 'days');

  return getCustomRangeObject(
    startDate.subtract(diff + 1, 'days').startOf('day'),
    startDate.subtract(1, 'days').startOf('day'),
  );
};

export const getPreviousRange = (range: DateRange): DateRange => {
  const previousRangeMap = {
    [Range.ThisWeek]: getCustomRangeObject(
      moment().subtract(1, 'week').startOf('week'),
      moment().subtract(1, 'week').endOf('week'),
    ),
    [Range.LastWeek]: getCustomRangeObject(
      moment().subtract(2, 'week').startOf('week'),
      moment().subtract(2, 'week').endOf('week'),
    ),
    [Range.ThisMonth]: getCustomRangeObject(
      moment().subtract(1, 'month').startOf('month'),
      moment().subtract(1, 'month').endOf('month'),
    ),
    [Range.LastMonth]: getCustomRangeObject(
      moment().subtract(2, 'month').startOf('month'),
      moment().subtract(2, 'month').endOf('month'),
    ),
    [Range.ThisQuarter]: getCustomRangeObject(
      moment().subtract(1, 'quarter').startOf('quarter'),
      moment().subtract(1, 'quarter').endOf('quarter'),
    ),
    [Range.LastQuarter]: getCustomRangeObject(
      moment().subtract(2, 'quarter').startOf('quarter'),
      moment().subtract(2, 'quarter').endOf('quarter'),
    ),
    [Range.Last30Days]: getCustomRangeObject(
      moment().subtract(60, 'days').startOf('day'),
      moment().subtract(30, 'days').startOf('day'),
    ),
    [Range.Last60Days]: getCustomRangeObject(
      moment().subtract(120, 'days').startOf('day'),
      moment().subtract(60, 'days').startOf('day'),
    ),
    [Range.Last90Days]: getCustomRangeObject(
      moment().subtract(180, 'days').startOf('day'),
      moment().subtract(90, 'days').startOf('day'),
    ),
    [Range.Last180Days]: getCustomRangeObject(
      moment().subtract(360, 'days').startOf('day'),
      moment().subtract(180, 'days').startOf('day'),
    ),
    [Range.ThisYear]: getCustomRangeObject(
      moment().subtract(1, 'year').startOf('year'),
      moment().subtract(1, 'year').endOf('year'),
    ),
    [Range.Anytime]: [moment(0), moment()],
    [Range.Custom]: getPreviousRangeForCustom(range as CustomRange),
  };

  const result = previousRangeMap[range.type];

  return result || unreachable(range as never);
};

export const getFormattedValueFromDateRange = (
  range: DateRange,
  format = DEFAULT_DATE_FORMAT,
): [string, string] =>
  getValueFromDateRange(range).map((x) => x.format(format)) as [string, string];
